import React, { useEffect, useState } from 'react';
import { ChevronLeft } from '@saleshandy/icons';
import moment from 'moment-timezone';

import Modal from '../../design-system/ui/modal';

import TimezoneDropdown from './components/timezone-dropdown';
import SuggestedSchedules from './components/suggested-schedules';
import ScheduleCondition from './components/schedule-condition';
import ScheduleReplyModalFooter from './components/schedule-reply-modal-footer';
import PickDateAndTime from './components/pick-date-and-time';

import { accessibleOnClick } from '../../utils/accessible-on-click';
import { ScheduleOnClick, TimezoneDropdownOption } from './types';
import type { IProps } from './schedule-reply-modal-container';
import {
  convertSameLocalTimeToDifferentTimezone,
  getTimezoneOption,
} from './utils/timezone';
import { executeOnRequestStatus, getIsRequestPending } from '../../utils';

const ScheduleReplyModal: React.FC<IProps> = ({
  show,
  onClose,
  onSchedule,
  userTimezone,
  emailId,
  sequenceId,
  unifiedScheduledId,

  getSequenceTimezoneRequestStatus,
  sequenceTimezone,
  replyScheduleRequestStatus,

  sendGetSequenceTimezoneRequest,
}) => {
  const [timeInputError, setTimeInputError] = useState(false);
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);

  const [isSequenceTimezone, setIsSequenceTimezone] = useState(
    !!sequenceTimezone,
  );

  // Creating Timezone Options
  // const sequenceTimezoneOption = getTimezoneOption(sequenceTimezone);
  // const userTimezoneOption = getTimezoneOption(userTimezone);

  const [
    selectedTimezone,
    setSelectedTimezone,
  ] = useState<TimezoneDropdownOption>(
    sequenceTimezone
      ? getTimezoneOption(sequenceTimezone)
      : getTimezoneOption(userTimezone),
  );
  const [scheduleConditionChecked, setScheduleConditionChecked] = useState(
    false,
  );
  const [scheduleDateTime, setScheduleDateTime] = useState<string>();
  const [selectedSuggestion, setSelectedSuggestion] = useState<string>('');
  const [initialSelectionFeedback, setInitialSelectionFeedback] = useState(
    true,
  );

  const setScheduleDateTimeValidate = (datetime: string) => {
    setInitialSelectionFeedback(false);
    setScheduleDateTime(datetime);
    if (moment(datetime).isBefore(moment())) {
      setTimeInputError(true);
    } else {
      setTimeInputError(false);
    }
  };

  useEffect(() => {
    if (show && !sequenceTimezone) {
      if (sequenceId) {
        sendGetSequenceTimezoneRequest({
          sequenceId,
          uniqueId: emailId,
        });
        return;
      }
      if (scheduleDateTime) {
        setScheduleDateTimeValidate(
          convertSameLocalTimeToDifferentTimezone(
            scheduleDateTime,
            selectedTimezone.name,
            sequenceTimezone,
          ),
        );
      }
    }
  }, [show]);

  useEffect(() => {
    executeOnRequestStatus({
      status: getSequenceTimezoneRequestStatus,
      onSuccess: () => {
        setSelectedTimezone(getTimezoneOption(sequenceTimezone));
        setIsSequenceTimezone(true);
        if (scheduleDateTime) {
          setScheduleDateTimeValidate(
            convertSameLocalTimeToDifferentTimezone(
              scheduleDateTime,
              selectedTimezone.name,
              sequenceTimezone,
            ),
          );
        }
      },
      // onFailed: () => {
      //   executeOnErrorWithErrorCheck({
      //     error: replyScheduleCancelRequestError,
      //     onError: () => {
      //       toaster.error(replyScheduleCancelRequestError?.message, {
      //         theme: Theme.New,
      //       });
      //     },
      //   });
      // },
    });
  }, [getSequenceTimezoneRequestStatus]);

  const onToggleDateTimePicker = () => {
    setShowDateTimePicker(!showDateTimePicker);
  };

  const onSave = () => {
    onSchedule({
      isSendEmailIfReplied: scheduleConditionChecked,
      timezone: selectedTimezone.name,
      date: scheduleDateTime,
    });
  };

  const onSuggestedScheduleClick: ScheduleOnClick = (schedule) => {
    setSelectedSuggestion(schedule.id);
    setScheduleDateTimeValidate(schedule.date);
  };

  const onSetSelectedTimezone = (value: TimezoneDropdownOption) => {
    setIsSequenceTimezone(sequenceTimezone === value.name);
    setSelectedTimezone(value);
    if (scheduleDateTime) {
      setScheduleDateTimeValidate(
        convertSameLocalTimeToDifferentTimezone(
          scheduleDateTime,
          selectedTimezone.name,
          value.name,
        ),
      );
    }
  };

  const onDateTimeChange = (datetime: string) => {
    setScheduleDateTimeValidate(datetime);
    setSelectedSuggestion(null);
  };

  return (
    <Modal
      show={show}
      title={
        showDateTimePicker ? (
          <div className="pick-date-time-header">
            <ChevronLeft {...accessibleOnClick(onToggleDateTimePicker)} />
            <span>Pick Date & Time</span>
          </div>
        ) : (
          'Schedule Reply'
        )
      }
      className="schedule-reply-modal"
      onHide={onClose}
      primaryBtnClickedHandler={onSave}
      primaryBtnDisabled={
        !scheduleDateTime ||
        timeInputError ||
        !unifiedScheduledId ||
        getIsRequestPending(replyScheduleRequestStatus)
      }
      primaryBtnLoading={getIsRequestPending(replyScheduleRequestStatus)}
      secondaryBtnClickedHandler={onClose}
      secondaryBtnDisabled={getIsRequestPending(replyScheduleRequestStatus)}
      footer={
        showDateTimePicker ? null : (
          <ScheduleReplyModalFooter
            onToggleDateTimePicker={onToggleDateTimePicker}
          />
        )
      }
    >
      {showDateTimePicker ? (
        <PickDateAndTime
          selectedTimezone={selectedTimezone}
          scheduleDateTime={scheduleDateTime}
          onDateTimeChange={onDateTimeChange}
          timeInputError={timeInputError}
        />
      ) : (
        <>
          <TimezoneDropdown
            selectedTimezone={selectedTimezone}
            setSelectedTimezone={onSetSelectedTimezone}
            isSequenceTimezone={isSequenceTimezone}
            getSequenceTimezoneRequestStatus={getSequenceTimezoneRequestStatus}
            sequenceTimezone={sequenceTimezone}
          />

          <SuggestedSchedules
            selectedTimezone={selectedTimezone}
            onClick={onSuggestedScheduleClick}
            selectedSuggestion={selectedSuggestion}
            initialSelectionFeedback={initialSelectionFeedback}
          />

          <ScheduleCondition
            scheduleConditionChecked={scheduleConditionChecked}
            setScheduleConditionChecked={setScheduleConditionChecked}
          />
        </>
      )}
    </Modal>
  );
};

export default ScheduleReplyModal;
